<!-- src/views/OnBoarding/OnboardingFormsView.vue -->
<template>
  <!-- Main Container: Center Aligned with Side Margins Matching Headers [1] -->
  <div class="max-w-7xl mx-auto px-4 sm:px-0 py-10 min-h-screen">
    <div class="space-y-6 animate-fadeIn text-left">
      
      <!-- LOADING STATE -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-24 bg-[#F0F0F0] rounded-[40px] border border-slate-100 shadow-sm">
        <div class="animate-spin h-10 w-10 border-4 border-[#20FCF1] border-t-transparent rounded-full mb-4"></div>
        <p class="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Synchronising Identity Registry...</p>
      </div>

      <!-- MAIN FORM GRID -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div 
          v-for="(form, index) in filteredForms" 
          :key="form.id"
          class="group relative bg-white rounded-[32px] p-8 border border-slate-100 shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col justify-between overflow-hidden"
        >
          <!-- STATUS ANCHOR LINE -->
          <div 
            class="absolute top-0 left-0 w-full h-1.5 transition-colors duration-500"
            :class="isCompleted(form.id) ? 'bg-[#20FCF1]' : 'bg-slate-100'"
          ></div>

          <div class="relative z-10">
            <div class="flex justify-between items-start mb-6">
              <div class="relative w-14 h-14 flex items-center justify-center">
                <!-- Dynamic Glow Layer matching the individual form's custom branding color [1.1.4] -->
                <div 
                  class="absolute inset-0 rounded-full blur-2xl opacity-10 group-hover:opacity-35 transition-opacity duration-700" 
                  :style="{ backgroundColor: form.status === 'completed' ? '#20FCF1' : form.color }"
                ></div>
                
                <svg class="absolute inset-0 w-full h-full drop-shadow-sm" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <!-- Path tint matching custom form brand color [1.1.4] -->
                  <path 
                    d="M12 2L3 7V12C3 17.5 7.1 21.6 12 23C16.9 21.6 21 17.5 21 12V7L12 2Z" 
                    :fill="form.status === 'completed' ? '#20FCF1' : form.color" 
                    :fill-opacity="form.status === 'completed' ? '0.15' : '0.05'" 
                    :stroke="form.status === 'completed' ? '#20FCF1' : '#E2E8F0'" 
                    stroke-width="1.5"
                  />
                </svg>
                <component 
                  :is="form.icon" 
                  class="w-6 h-6 relative z-10 transition-colors" 
                  :style="{ color: isCompleted(form.id) ? '#20FCF1' : form.color }"
                />
              </div>
              
              <!-- Highly Visible Protocol Numbers in Brand Mint (Corrected for Double-Digit 10 formatting) [1] -->
              <div class="text-right">
                <span class="text-[8px] font-black text-slate-400 uppercase tracking-widest block leading-none">Protocol</span>
                <span class="text-xl font-mono font-black text-[#20FCF1] block leading-none mt-1.5">
                  {{ index + 1 < 10 ? '0' + (index + 1) : index + 1 }}
                </span>
              </div>
            </div>

            <h3 class="text-lg font-black text-[#0A1630] uppercase tracking-tight leading-tight mb-2">
              {{ form.title }}
            </h3>
            <p class="text-xs text-slate-500 font-medium leading-relaxed mb-8">
              {{ form.description }}
            </p>
          </div>

          <div class="relative z-10 flex items-center justify-between gap-4 mt-auto">
            <!-- STATUS INDICATORS -->
            <div v-if="isCompleted(form.id)" class="flex items-center gap-2 bg-[#EBFDF8] px-4 py-2 rounded-full border border-[#20FCF1]/20">
              <CheckCircleIcon class="h-4 w-4 text-[#20FCF1]" />
              <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest">Verified</span>
            </div>
            
            <!-- Orange Pending Status badge [1] -->
            <div v-else class="flex items-center gap-2 bg-amber-50 px-4 py-2 rounded-full border border-amber-200/50">
              <ClockIcon class="h-4 w-4 text-amber-500 animate-pulse" />
              <span class="text-[9px] font-black text-amber-500 uppercase tracking-widest">Pending</span>
            </div>

            <!-- ACTION BUTTON -->
            <router-link 
              :to="getFormRoute(form)"
              class="px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-md active:scale-95"
              :class="isCompleted(form.id) 
                ? 'bg-[#0A1630] text-white hover:bg-slate-800' 
                : 'bg-[#20FCF1] text-[#0A1630] hover:shadow-[#20FCF1]/20 hover:shadow-xl'"
            >
              {{ isCompleted(form.id) ? 'Review' : 'Initiate' }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { db, auth } from '@/firebaseConfig';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { 
  UserIcon, IdentificationIcon, ScaleIcon, 
  PencilSquareIcon, DocumentTextIcon, HeartIcon, 
  EyeIcon, BriefcaseIcon, WrenchScrewdriverIcon, 
  BookOpenIcon, CheckCircleIcon, ClockIcon,
  ShieldCheckIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const loading = ref(true);
const submissions = ref([]);

/** 🛡️ PROTOCOL REGISTRY: ALL POSSIBLE ONBOARDING FORMS (Rearranged and renamed) */
const allForms = [
  { id: 'yourDetails', title: 'Your Details', description: 'Personal information, emergency contacts and medical data.', icon: UserIcon, route: '/forms/your-details', color: '#3B82F6' },
  { id: 'employeeInduction', title: 'Induction', description: 'Core health and safety protocol briefing.', icon: IdentificationIcon, route: '/forms/employee-induction', color: '#F97316' },
  { id: 'rolesAndResponsibilities', title: 'Responsibility Acknowledgement', description: 'Definition of operational roles and corporate responsibilities.', icon: ScaleIcon, route: '/forms/roles-responsibilities', color: '#8B5CF6' },
  { id: 'literacyTest', title: 'Literacy Test', description: 'Linguistic and literacy competency assessment.', icon: PencilSquareIcon, route: '/forms/literacy-test', color: '#EC4899' },
  { id: 'annualHealthAssessment', title: 'Health Assessment', description: 'Annual health and fitness-for-work certification.', icon: HeartIcon, route: '/forms/annual-health-assessment', color: '#EF4444' },
  { id: 'eyesightDeclaration', title: 'Eyesight Declaration', description: 'Compliance with railway engineering visual requirements.', icon: EyeIcon, route: '/forms/eyesight-declaration', color: '#06B6D4' },
  { id: 'rightToWork', title: 'Right to Work', description: 'Proof of citizenship or valid working visa documentation.', icon: BriefcaseIcon, route: '/forms/right-to-work', color: '#F59E0B' },
  { id: 'ppeRequest', title: 'PPE Issue', description: 'Protective gear size requirements and issue log.', icon: WrenchScrewdriverIcon, route: '/forms/ppe-request', color: '#10B981' },
  { id: 'primarySponsorContract', title: 'Primary Sponsor Contract', description: 'Contractual agreement for primary sentinel sponsorship.', icon: DocumentTextIcon, route: '/forms/primary-sponsor-contract', color: '#6366F1' },
  { id: 'employeeHandbookSignOff', title: 'Employee Handbook Acknowledgement', description: 'Formal acceptance of the internal employee handbook.', icon: BookOpenIcon, route: '/forms/employee-handbook-sign-off', color: '#D946EF' },
  { id: 'subContractorPack', title: 'Sub-Contractor Pack', description: 'Primary compliance pack for contractual entities.', icon: ShieldCheckIcon, route: '/forms/sub-contractor-pack', color: '#475569' }
];

/** 🛡️ MISSION CRITICAL: CONDITIONAL VISIBILITY HANDSHAKE
 * Enforces the Onboarding Visibility protocols dynamically.
 */
const filteredForms = computed(() => {
  const profile = userStore.userProfile;
  if (!profile) return [];

  const type = (profile.employmentType || '').toLowerCase().trim();
  const isPrimarySponsor = String(profile.sponsorshipType || '').trim().toLowerCase() === 'primary';
  
  // 1. Sub-Contractor Protocol (1 Form only)
  if (type.includes('sub') && type.includes('contractor')) {
    return allForms.filter(f => f.id === 'subContractorPack');
  }

  // 2. Standard Workers filtering (Employee or Self-Employed)
  return allForms.filter(f => {
    // Sub-contractor packet is hidden from standard workers
    if (f.id === 'subContractorPack') return false;

    // Form 9 (Primary Sponsor Contract) is strictly hidden from non-primary sponsor users
    if (f.id === 'primarySponsorContract') {
      return isPrimarySponsor;
    }

    // Form 10 (Employee Handbook) is only displayed for Employees (hidden for Self-Employed)
    if (f.id === 'employeeHandbookSignOff') {
      return type === 'employee';
    }

    return true;
  });
});

const isCompleted = (formId) => {
  return submissions.value.some(s => s.formIdentifier === formId && (s.status === 'Completed' || s.status === 'Pending'));
};

// Resolves target routes dynamically supporting direct routing and completed form views
const getFormRoute = (form) => {
  if (isCompleted(form.id)) {
    const sub = submissions.value.find(s => s.formIdentifier === form.id && (s.status === 'Completed' || s.status === 'Pending'));
    if (sub && sub.id) {
      return `/completed-form/onboardingFormSubmissions/${sub.id}`;
    }
  }
  return form.route;
};

const fetchSubmissions = async () => {
  loading.value = true;
  try {
    const userId = userStore.userProfile?.userId;
    if (!userId) return;
    const q = query(collection(db, 'onboardingFormSubmissions'), where('userId', '==', userId));
    const snap = await getDocs(q);
    submissions.value = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error("Registry Hydration Failure:", e);
  } finally {
    loading.value = false;
  }
};

// Hardened watcher executing only when auth session AND userProfile have completely synchronized
watch(
  () => [userStore.isAuthReady, userStore.isUserProfileLoading],
  async ([isReady, isProfileLoading]) => {
    if (!isReady || isProfileLoading || !auth.currentUser) { 
      return; 
    }
    await fetchSubmissions();
  },
  { immediate: true }
);

onMounted(() => {
  if (userStore.isAuthReady && !userStore.isUserProfileLoading) {
    fetchSubmissions();
  }
});
</script>

<style scoped>
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>